import { AlertTriangle } from 'lucide-react';

// @ts-ignore - JSX component
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

/**
 * Confirmarea stergerii unei cereri de inscriere.
 *
 * Inlocuieste un `window.confirm()` nativ. Trei motive, in ordinea importantei:
 *  1. caseta de sistem arata strain intr-un UI redesenat si nu respecta nici
 *     tokenii, nici pragul de 44px pe telefon;
 *  2. `confirm()` blocheaza complet firul, deci si canalul CDP — orice harness
 *     care apasa butonul ingheata cu „Runtime.callFunctionOn timed out", care
 *     seamana cu o pagina lenta, nu cu un dialog. Ne-a costat doua rulari;
 *  3. nu se poate verifica pe ecran: nu exista in DOM, deci nicio sonda nu-l vede.
 *
 * NU foloseste `AlertDialog` din shadcn: acela n-are fisier in `components/ui/`,
 * iar adaugarea lui ar fi o schimbare de fundatie. `Dialog` exista deja si e
 * folosit de toate celelalte dialoguri din domeniu.
 */
interface DeleteConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    /** Numele scolii, ca utilizatorul sa vada CE sterge, nu doar „aceasta cerere". */
    schoolName?: string;
    loading?: boolean;
}

export function DeleteConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    schoolName,
    loading = false,
}: DeleteConfirmDialogProps) {
    return (
        /* `onOpenChange` primeste un BOOLEAN, iar `onClose: () => void` il ignora:
           varianta de dinainte chema `onClose()` si la DESCHIDERE. Masurat cu un
           `DialogTrigger` adaugat temporar — Radix emite `onOpenChange(true)`, si
           din el iesea un apel parazit la `onClose`; cu garda, zero. Azi nu se vede,
           pentru ca dialogul n-are trigger si parintele comuta singur `isOpen` —
           adica exact conditia care ar disparea la prima adaugare de trigger.
           Acelasi defect si aceeasi reparatie ca in `PhoneNotesDialog` (#47). */
        <Dialog open={isOpen} onOpenChange={(deschis) => { if (!deschis) onClose(); }}>
            <DialogContent className="sm:max-w-[440px]">
                <DialogHeader>
                    <DialogTitle>Ștergerea cererii de înscriere</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* `/5`, nu `/10`: `text-error` (#B91C1C) pe `bg-error/10` scade sub prag.

                        Chenarul e PLIN, nu `/20`: caseta anunta CONSECINTA unei actiuni care nu
                        s-a intamplat inca, deci trebuie sa se vada ca o placuta, nu ca un rand de
                        text. Cu `/20`, compus peste umplutura, chenarul iesea la 1,40 contrast
                        interior si 1,53 exterior — desenat, dar invizibil. Plin, ajunge la 5,92 /
                        6,47, adica exact cifrele lui `text-error`: aceeasi culoare.
                        Regula, formulata de S1 si valabila in tot frontendul: caseta de dinaintea
                        actiunii primeste chenar plin; cea care raporteaza ce s-a intamplat DEJA
                        (`WasteCollectionFormPage`, `CompletionScreen`, `ImageUploadReport`) ramane
                        rand cu pictograma si text, cu umplutura care duce mesajul. */}
                    <div className="flex items-start gap-2 rounded-md border border-error bg-error/5 p-3">
                        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-error" aria-hidden />
                        <p className="text-small text-error">Această acțiune nu poate fi anulată.</p>
                    </div>

                    <p className="text-small text-gray-900">
                        {schoolName
                            ? <>Se șterge cererea de înscriere a școlii <span className="font-semibold break-words">{schoolName}</span>.</>
                            : 'Se șterge această cerere de înscriere.'}
                    </p>

                    {/* Sub `sm` pe coloana, cu actiunea distructiva jos, langa deget.
                        Inaltimea de 44px sub `md` vine din `Button` (cva), nu de aici. */}
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <Button variant="outline" onClick={onClose} disabled={loading}>
                            Anulare
                        </Button>
                        <Button variant="destructive" onClick={onConfirm} disabled={loading}>
                            {loading ? 'Se șterge...' : 'Șterge cererea'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
